/**
 * Standardized loading state component
 * Provides consistent loading UI across the application
 */

import React from 'react';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import Fade from '@mui/material/Fade';
import { logger } from '../../utils/logger';

const LoadingState = ({
  message = 'Loading...',
  size = 40,
  showMessage = true,
  fullHeight = false,
  compact = false,
  delay = 0,
  disableAnimation = false,
  component = 'LoadingState'
}) => {
  const [show, setShow] = React.useState(delay === 0);

  React.useEffect(() => {
    if (delay > 0) {
      const timer = setTimeout(() => {
        setShow(true);
        logger.info(component, 'Loading state shown after delay', { delay });
      }, delay);

      return () => clearTimeout(timer);
    }
    return undefined;
  }, [delay, component]);

  if (!show) return null;

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: fullHeight ? '400px' : (compact ? '72px' : '200px'),
        p: compact ? 1 : 3
      }}
    >
      <CircularProgress
        size={size}
        sx={{ mb: showMessage ? 2 : 0 }}
      />
      {showMessage && (
        <Typography
          variant="body1"
          color="text.secondary"
          align="center"
        >
          {message}
        </Typography>
      )}
    </Box>
  );

  if (disableAnimation) {
    return content;
  }

  return (
    <Fade in={show} timeout={300}>
      {content}
    </Fade>
  );
};

export default LoadingState;
